import React, { useState, useEffect, useContext, createContext, useRef, useCallback } from 'react';

import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, X, Sun, Moon, Monitor, BarChart3, User, Briefcase, 
  Mail, Grid3X3, ChevronRight, ExternalLink, Star, Award,
  Calendar, MapPin, Globe, Download, Eye, Phone, ChevronLeft,
  Home, UserCircle, Image, Settings, MessageSquare // BookOpen temporarily removed
} from 'lucide-react';
import logoSvg from './assets/logo.svg';
import axios from 'axios';
import { getAuthToken } from './utils/sessionAuth';
import './App.css';

// Import new components
import Gallery from './components/Gallery';
import DashboardDetail from './components/DashboardDetail';
import AdminPanel from './components/admin/AdminPanel';
// Blog components temporarily removed
// import BlogPage from './components/BlogPage';
// import BlogPostDetail from './components/BlogPostDetail';
// import { BlogEditor } from './components/editor';
import ErrorBoundary, { NetworkErrorFallback } from './components/ErrorBoundary';
import LazyImage from './components/LazyImage';

// Import enhanced utilities
import { apiClient, clearCache } from './utils/apiEnhanced';
import config, { debugLog, errorLog, getApiConfig } from './config/environment';
import { getApiUrl, API, BACKEND_URL_EXPORT as BACKEND_URL } from './utils/api';
import { initializeVisitorTracking } from './utils/visitorTracking';

// Theme Context
const ThemeContext = React.createContext();

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved || 'light';
  });

  useEffect(() => {
    localStorage.setItem('theme', theme);
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

const useTheme = () => React.useContext(ThemeContext);

// Contact Context
const ContactContext = createContext();

const ContactProvider = ({ children }) => {
  const [contactInfo, setContactInfo] = useState({
    linkedin: '',
    whatsapp: '',
    fabricCommunity: '',
    email: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch contact info from backend
  const fetchContactInfo = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch("https://nwdrkcdpfhbtrbqmydyg.supabase.co/functions/v1/contact-info", { 
        method: "GET",
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error("Failed to fetch contact info");
      const data = await response.json();
      setContactInfo(data);
      localStorage.setItem("contactInfo", JSON.stringify(data));
    } catch (err) {
      setError(err.message);
      const cached = localStorage.getItem("contactInfo");
      if (cached) setContactInfo(JSON.parse(cached));
    } finally {
      setLoading(false);
    }
  }, []);

  // Update contact info in backend
  const updateContactInfo = async (newInfo) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch("https://nwdrkcdpfhbtrbqmydyg.supabase.co/functions/v1/contact-info", {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify(newInfo),
      });
      if (!response.ok) throw new Error("Failed to update contact info");
      
      // The response is just {success: true}, so we need to use the newInfo directly
      // instead of using the response data
      setContactInfo(newInfo);
      localStorage.setItem("contactInfo", JSON.stringify(newInfo));
      
      // Fetch the updated data from the server to ensure consistency
      fetchContactInfo();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchContactInfo();
  }, [fetchContactInfo]);

  return (
    <ContactContext.Provider value={{ contactInfo, loading, error, updateContactInfo, refetch: fetchContactInfo }}>
      {children}
    </ContactContext.Provider>
  );
};

const useContact = () => {
  const context = useContext(ContactContext);
  if (!context) {
    throw new Error('useContact must be used within a ContactProvider');
  }
  return context;
};

// Export useContact for use in other components
export { useContact };

// Page Content Context
const PageContentContext = createContext();

const PageContentProvider = ({ children }) => {
  const [aboutMeContent, setAboutMeContent] = useState({
    title: 'About Me',
    intro: '',
    professional_journey: '',
    skills: []
  });
  const [servicesContent, setServicesContent] = useState({
    title: 'Services',
    subtitle: '',
    services: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch About Me content from backend
  const fetchAboutMeContent = React.useCallback(async () => {
    try {
      const response = await fetch("https://nwdrkcdpfhbtrbqmydyg.supabase.co/functions/v1/about-me-content", {
        method: "GET",
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error("Failed to fetch about me content");
      const data = await response.json();
      setAboutMeContent(data);
      localStorage.setItem("aboutMeContent", JSON.stringify(data));
    } catch (err) {
      console.error('Error fetching about me content:', err);
      const cached = localStorage.getItem("aboutMeContent");
      if (cached) setAboutMeContent(JSON.parse(cached));
    }
  }, []);

  // Fetch Services content from backend
  const fetchServicesContent = React.useCallback(async () => {
    try {
      const response = await fetch("https://nwdrkcdpfhbtrbqmydyg.supabase.co/functions/v1/services-content", {
        method: "GET",
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error("Failed to fetch services content");
      const data = await response.json();
      setServicesContent(data);
      localStorage.setItem("servicesContent", JSON.stringify(data));
    } catch (err) {
      console.error('Error fetching services content:', err);
      const cached = localStorage.getItem("servicesContent");
      if (cached) setServicesContent(JSON.parse(cached));
    }
  }, []);

  // Update About Me content in backend
  const updateAboutMeContent = async (newContent) => {
    setLoading(true);
    setError(null);
    try {
      const token = getAuthToken();
      const response = await fetch("https://nwdrkcdpfhbtrbqmydyg.supabase.co/functions/v1/about-me-content", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newContent),
      });
      if (!response.ok) throw new Error("Failed to update about me content");
      
      setAboutMeContent(newContent);
      localStorage.setItem("aboutMeContent", JSON.stringify(newContent));
      fetchAboutMeContent();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update Services content in backend
  const updateServicesContent = async (newContent) => {
    setLoading(true);
    setError(null);
    try {
      const token = getAuthToken();
      const response = await fetch("https://nwdrkcdpfhbtrbqmydyg.supabase.co/functions/v1/services-content", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newContent),
      });
      if (!response.ok) throw new Error("Failed to update services content");
      
      setServicesContent(newContent);
      localStorage.setItem("servicesContent", JSON.stringify(newContent));
      fetchServicesContent();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchAboutMeContent();
    fetchServicesContent();
  }, [fetchAboutMeContent, fetchServicesContent]);

  return (
    <PageContentContext.Provider value={{
      aboutMeContent,
      servicesContent,
      loading,
      error,
      updateAboutMeContent,
      updateServicesContent,
      refetchAboutMe: fetchAboutMeContent,
      refetchServices: fetchServicesContent
    }}>
      {children}
    </PageContentContext.Provider>
  );
};

const usePageContent = () => {
  const context = useContext(PageContentContext);
  if (!context) {
    throw new Error('usePageContent must be used within a PageContentProvider');
  }
  return context;
};

// Export usePageContent for use in other components
export { usePageContent };

// Dashboard Carousel Component
const DashboardCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [featuredDashboards, setFeaturedDashboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slideDirection, setSlideDirection] = useState(null); // Track slide direction for animation
  
  // Fallback images for when no featured dashboards are available
  const fallbackImages = [
    {
      src: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=500&fit=crop',
      alt: 'Executive Dashboard',
      fallback: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=500&fit=crop',
      slug: null
    },
    {
      src: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop',
      alt: 'Financial Overview Dashboard',
      fallback: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop',
      slug: null
    },
    {
      src: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=400&fit=crop',
      alt: 'Performance Dashboard',
      fallback: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=400&fit=crop',
      slug: null
    }
  ];

  useEffect(() => {
    const fetchFeaturedDashboards = async () => {
      console.log('API URL:', API);
      console.log('BACKEND_URL:', BACKEND_URL);
      
      try {
        const response = await axios.get(getApiUrl('dashboards/featured'));
        console.log('Featured dashboards response:', response.data);
        console.log('Response status:', response.status);
        
        // Handle nested Supabase Edge Function response structure
        const dashboardsData = response.data?.data || response.data;
        console.log('Dashboards data:', dashboardsData);
        console.log('Is array:', Array.isArray(dashboardsData));
        console.log('Data length:', Array.isArray(dashboardsData) ? dashboardsData.length : 'Not an array');
        
        if (dashboardsData && Array.isArray(dashboardsData) && dashboardsData.length > 0) {
          const dashboardImages = dashboardsData.map(dashboard => {
            // Normalize field names to handle both camelCase and lowercase variations
            const normalizedMainImage = dashboard.mainImage || dashboard.mainimage || dashboard['mainImage'];
            console.log('Processing dashboard:', dashboard.title, 'mainImage:', normalizedMainImage);
            return {
              src: normalizedMainImage || 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=500&fit=crop',
              alt: dashboard.title,
              fallback: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=500&fit=crop',
              slug: dashboard.slug,
              title: dashboard.title
            };
          });
          setFeaturedDashboards(dashboardImages);
          console.log('Set featured dashboards:', dashboardImages);
        } else {
          console.log('No featured dashboards found, using fallback images');
          setFeaturedDashboards(fallbackImages);
        }
      } catch (error) {
        console.error('Error fetching featured dashboards:', error);
        console.error('Error details:', error.response?.data);
        console.error('Error status:', error.response?.status);
        setFeaturedDashboards(fallbackImages);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedDashboards();
  }, []);

  const dashboardImages = featuredDashboards.length > 0 ? featuredDashboards : fallbackImages;

  // Auto-play carousel - move to next slide every 3 seconds
  useEffect(() => {
    if (loading || dashboardImages.length <= 1) return;
    
    const interval = setInterval(() => {
      nextSlide();
    }, 3000);

    return () => clearInterval(interval);
  }, [loading, dashboardImages.length]);

  const nextSlide = () => {
    setSlideDirection('right');
    setCurrentIndex((prev) => (prev + 1) % dashboardImages.length);
    
    // Reset direction after animation completes
    setTimeout(() => setSlideDirection(null), 600);
  };

  const prevSlide = () => {
    setSlideDirection('left');
    setCurrentIndex((prev) => (prev - 1 + dashboardImages.length) % dashboardImages.length);
    
    // Reset direction after animation completes
    setTimeout(() => setSlideDirection(null), 600);
  };

  const getPrevIndex = () => (currentIndex - 1 + dashboardImages.length) % dashboardImages.length;
  const getNextIndex = () => (currentIndex + 1) % dashboardImages.length;

  if (loading) {
    return (
      <div className="dashboard-carousel-container relative w-full h-[550px] flex items-center justify-center">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-carousel-container relative w-full h-[550px] flex items-center justify-center py-8">
      {/* Remove the title from outside the frame */}
      
      {/* Left Arrow */}
      <button
        onClick={prevSlide}
        className="absolute left-4 z-30 p-3 rounded-full bg-white/90 dark:bg-gray-800/90 shadow-lg hover:bg-white dark:hover:bg-gray-800 transition-all duration-300 hover:scale-110 group"
        aria-label="Previous dashboard"
      >
        <ChevronLeft className="w-6 h-6 text-gray-700 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
      </button>

      {/* Carousel Container */}
      <div className="relative w-full max-w-5xl h-full flex items-center justify-center perspective-1000">
        {/* Previous Image (Left) - Positioned more inside */}
        <motion.div
          key={`prev-${getPrevIndex()}`}
          initial={{ opacity: 0, x: -40, scale: 0.7 }}
          animate={{ opacity: 0.7, x: -60, scale: 0.8 }}
          exit={{ opacity: 0, x: -80, scale: 0.7 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          className="absolute left-0 hidden md:block z-10"
        >
          <div className="w-64 h-48 rounded-lg overflow-hidden shadow-lg transform -rotate-6 hover:rotate-0 transition-transform duration-300 opacity-80 hover:opacity-100">
            <div className="w-full h-full relative overflow-hidden">
              <img
                src={dashboardImages[getPrevIndex()].src}
                alt={dashboardImages[getPrevIndex()].alt}
                className="w-[200%] h-full object-contain bg-white dark:bg-gray-800 absolute right-0"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = dashboardImages[getPrevIndex()].fallback;
                }}
              />
            </div>
          </div>
        </motion.div>

        {/* Current Image (Center) - Larger and enhanced */}
        <motion.div
          key={`current-${currentIndex}`}
          initial={{ 
            opacity: 0, 
            scale: 0.9,
            x: slideDirection === 'left' ? 50 : slideDirection === 'right' ? -50 : 0 
          }}
          animate={{ 
            opacity: 1, 
            scale: 1,
            x: 0 
          }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          className="relative z-20"
        >
          {dashboardImages[currentIndex].slug ? (
            <Link to={`/dashboard/${dashboardImages[currentIndex].slug}`}>
              <div className="w-[320px] md:w-[520px] lg:w-[600px] h-[240px] md:h-[390px] lg:h-[450px] rounded-xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-300 cursor-pointer group bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700">
                {/* Dashboard Title - Inside the frame and fixed */}
                <div className="absolute top-0 left-0 right-0 z-30 bg-white/80 dark:bg-gray-800/80 py-2 px-3 text-center border-b border-gray-100 dark:border-gray-700">
                  <h3 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white">
                    {dashboardImages[currentIndex].title || dashboardImages[currentIndex].alt}
                  </h3>
                </div>
                <img
                  src={dashboardImages[currentIndex].src}
                  alt={dashboardImages[currentIndex].alt}
                  className="w-full h-full object-contain p-2 pt-12 group-hover:brightness-105 transition-all duration-300"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = dashboardImages[currentIndex].fallback;
                  }}
                />
                <div className="absolute inset-0 pt-10 bg-black/0 group-hover:bg-black/10 transition-all duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 dark:bg-gray-900/90 px-4 py-2 rounded-lg shadow-lg transform translate-y-2 group-hover:translate-y-0">
                    <span className="text-gray-900 dark:text-white font-medium flex items-center">
                      <Eye className="w-4 h-4 mr-2" />
                      View Dashboard
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ) : (
            <div className="w-[320px] md:w-[520px] lg:w-[600px] h-[240px] md:h-[390px] lg:h-[450px] rounded-xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-300 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700">
              {/* Dashboard Title - Inside the frame and fixed */}
              <div className="absolute top-0 left-0 right-0 z-30 bg-white/80 dark:bg-gray-800/80 py-2 px-3 text-center border-b border-gray-100 dark:border-gray-700">
                <h3 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white">
                  {dashboardImages[currentIndex].title || dashboardImages[currentIndex].alt}
                </h3>
              </div>
              <img
                src={dashboardImages[currentIndex].src}
                alt={dashboardImages[currentIndex].alt}
                className="w-full h-full object-contain p-2 pt-12"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = dashboardImages[currentIndex].fallback;
                }}
              />
            </div>
          )}
        </motion.div>

        {/* Next Image (Right) - Positioned more inside */}
        <motion.div
          key={`next-${getNextIndex()}`}
          initial={{ opacity: 0, x: 40, scale: 0.7 }}
          animate={{ opacity: 0.7, x: 60, scale: 0.8 }}
          exit={{ opacity: 0, x: 80, scale: 0.7 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          className="absolute right-0 hidden md:block z-10"
        >
          <div className="w-64 h-48 rounded-lg overflow-hidden shadow-lg transform rotate-6 hover:rotate-0 transition-transform duration-300 opacity-80 hover:opacity-100">
            <div className="w-full h-full relative overflow-hidden">
              <img
                src={dashboardImages[getNextIndex()].src}
                alt={dashboardImages[getNextIndex()].alt}
                className="w-[200%] h-full object-contain bg-white dark:bg-gray-800 absolute left-0"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = dashboardImages[getNextIndex()].fallback;
                }}
              />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Right Arrow */}
      <button
        onClick={nextSlide}
        className="absolute right-4 z-30 p-3 rounded-full bg-white/90 dark:bg-gray-800/90 shadow-lg hover:bg-white dark:hover:bg-gray-800 transition-all duration-300 hover:scale-110 group"
        aria-label="Next dashboard"
      >
        <ChevronRight className="w-6 h-6 text-gray-700 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
      </button>

      {/* Dots Indicator */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-30">
        {dashboardImages.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === currentIndex
                ? 'bg-blue-600 dark:bg-blue-400 scale-125'
                : 'bg-white/60 dark:bg-gray-600/60 hover:bg-white/80 dark:hover:bg-gray-400/80'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();

  const navItems = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/about', label: 'About Me', icon: UserCircle },
    { path: '/gallery', label: 'Dashboard Gallery', icon: BarChart3 },
    // { path: '/blog', label: 'Blog', icon: BookOpen }, // Temporarily removed
    { path: '/services', label: 'Services', icon: Briefcase },
    { path: '/contact', label: 'Contact', icon: MessageSquare },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 dark:bg-gray-900/90 backdrop-blur-lg border-b border-gray-200 dark:border-gray-700 shadow-lg">
      <div className="w-full">
        <div className="flex items-center justify-between h-20 px-4 md:px-6 lg:px-8">
          <Link to="/" className="flex items-center space-x-2 sm:space-x-3 group">
            <motion.div
              className="relative"
              whileHover={{ 
                rotate: 360,
                scale: 1.3,
                transition: { 
                  type: "spring",
                  stiffness: 300,
                  damping: 20,
                  duration: 0.6
                }
              }}
            >
              <img 
                src={logoSvg} 
                alt="Arbaz Ahmad Logo" 
                className="w-12 h-12 sm:w-16 sm:h-16 logo-shimmer"
              />
            </motion.div>
            <span className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white" style={{textShadow: '1px 1px 0px rgba(0,0,0,0.3)'}}>
              Arbaz Ahmad
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {navItems.map(({ path, label, icon: Icon }) => (
              <motion.div
                key={path}
                whileHover={{ 
                  scale: 1.05,
                  y: -2,
                  transition: { type: "spring", stiffness: 400, damping: 25 }
                }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to={path}
                  className={`flex items-center space-x-2 px-4 py-3 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md ${
                    location.pathname === path
                      ? 'text-blue-600 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 border border-blue-200 dark:border-blue-700 shadow-md'
                      : 'text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50 dark:hover:from-gray-800 dark:hover:to-blue-900/20 border border-transparent hover:border-gray-200 dark:hover:border-gray-600'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{label}</span>
                </Link>
              </motion.div>
            ))}
            <motion.button
              onClick={toggleTheme}
              whileHover={{ 
                scale: 1.1,
                rotate: 180,
                transition: { type: "spring", stiffness: 300, damping: 20 }
              }}
              whileTap={{ scale: 0.9 }}
              className="p-3 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 text-gray-700 dark:text-gray-300 hover:from-yellow-100 hover:to-orange-100 dark:hover:from-blue-800 dark:hover:to-purple-800 transition-all duration-300 shadow-sm hover:shadow-md border border-gray-200 dark:border-gray-600"
            >
              {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </motion.button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-3">
            <motion.button
              onClick={toggleTheme}
              whileHover={{ scale: 1.1, rotate: 180 }}
              whileTap={{ scale: 0.9 }}
              className="p-3 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 shadow-sm hover:shadow-md transition-all duration-300"
            >
              {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </motion.button>
            <motion.button
              onClick={() => setIsOpen(!isOpen)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="p-3 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 shadow-sm hover:shadow-md transition-all duration-300"
            >
              <motion.div
                animate={{ rotate: isOpen ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </motion.div>
            </motion.button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden py-4 border-t border-gray-200 dark:border-gray-700"
            >
              {navItems.map(({ path, label, icon: Icon }) => (
                <motion.div
                  key={path}
                  whileHover={{ x: 10, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Link
                    to={path}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center space-x-4 px-6 py-4 mx-2 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md ${
                      location.pathname === path
                        ? 'text-blue-600 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 border-l-4 border-blue-500'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50 dark:hover:from-gray-800 dark:hover:to-blue-900/20'
                    }`}
                  >
                    <Icon className="w-6 h-6" />
                    <span className="font-medium text-lg">{label}</span>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

// Power BI Preview Component
const PowerBIPreview = ({ title = "Featured Dashboard", className = "" }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [randomDashboard, setRandomDashboard] = useState(null);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    const fetchRandomDashboard = async () => {
      try {
        setIsLoading(true);
        setFetchError(false);
        
        debugLog('Fetching random dashboard with Power BI embed link');
        
        // Use enhanced API client with retry logic and caching
        const responseData = await apiClient.get('dashboards?limit=100');
        
        // Handle Supabase Edge Function response structure
        const dashboards = responseData.data || responseData;
        
        if (!Array.isArray(dashboards)) {
          throw new Error('Invalid dashboard data format received');
        }
        
        // Normalize field names and filter dashboards that have valid Power BI embed links
        const dashboardsWithLinks = dashboards
          .map(dashboard => ({
            ...dashboard,
            // Normalize PowerBI link field variations
            powerBIEmbedLink: dashboard.powerBIEmbedLink || dashboard.powerbiembedlink || dashboard['powerBIEmbedLink']
          }))
          .filter(dashboard => 
            dashboard.powerBIEmbedLink && 
            dashboard.powerBIEmbedLink.trim() !== ''
          );
        
        if (dashboardsWithLinks.length > 0) {
          // Select a random dashboard
          const randomIndex = Math.floor(Math.random() * dashboardsWithLinks.length);
          setRandomDashboard(dashboardsWithLinks[randomIndex]);
          debugLog('Random dashboard selected:', dashboardsWithLinks[randomIndex].title);
        } else {
          errorLog('No dashboards with Power BI embed links found');
          setFetchError(true);
        }
      } catch (error) {
        errorLog('Error fetching random dashboard:', error);
        setFetchError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRandomDashboard();
  }, []); // Empty dependency array means this runs once on component mount

  if (fetchError || !randomDashboard) {
    return (
      <div className={`powerbi-embed-container bg-white dark:bg-gray-800 rounded-xl shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-700 ${className}`}>
        <div className="p-3 md:p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              <BarChart3 size={20} className="mr-2 md:mr-3 text-blue-600 dark:text-blue-400 md:w-7 md:h-7" />
              <span className="truncate">{title}</span>
            </h2>
          </div>
          <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mt-1 md:mt-2">Interactive dashboard with real-time data visualization</p>
        </div>
        <div className="relative h-[700px] md:h-[850px] lg:h-[900px] flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-600 dark:text-gray-300 mb-4 text-lg">Unable to load dashboard preview</p>
            <button 
              onClick={() => window.location.reload()} 
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-200"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`powerbi-embed-container bg-white dark:bg-gray-800 rounded-xl shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-700 ${className}`}>
      <div className="p-3 md:p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white flex items-center">
            <BarChart3 size={20} className="mr-2 md:mr-3 text-blue-600 dark:text-blue-400 md:w-7 md:h-7" />
            <span className="truncate">{randomDashboard.title ? `${randomDashboard.title} | Preview` : `${title} | Preview`}</span>
          </h2>
          <div className="flex items-center space-x-1 md:space-x-2">
            <span className="inline-flex items-center px-2 md:px-3 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
              <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-1 md:mr-2 animate-pulse"></div>
              <span className="hidden sm:inline">Live Data</span>
              <span className="sm:hidden">Live</span>
            </span>
          </div>
        </div>
        <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mt-1 md:mt-2">Interactive dashboard with real-time data visualization</p>
        {randomDashboard.category && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Category: {randomDashboard.category}
          </p>
        )}
      </div>
      <div className="relative aspect-video w-full">
        {isLoading && (
          <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 flex flex-col items-center justify-center z-10">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 dark:border-gray-600 dark:border-t-blue-400"></div>
            <p className="mt-4 text-lg font-medium text-gray-700 dark:text-gray-300">Loading Interactive Dashboard...</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Please wait while we prepare your data visualization</p>
          </div>
        )}
        <iframe
          title={`${randomDashboard.title || title} Preview`}
          src={randomDashboard.powerBIEmbedLink}
          frameBorder="0"
          allowFullScreen={true}
          className="w-full h-full absolute top-0 left-0 rounded-b-xl"
          onLoad={() => setIsLoading(false)}
          onError={() => {
            setIsLoading(false);
            setFetchError(true);
          }}
        ></iframe>
      </div>
    </div>
  );
};

// Home Page
const HomePage = () => {
  const [dashboards, setDashboards] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchRandomDashboards = async () => {
      try {
        setIsLoading(true);
        
        debugLog('Fetching random dashboards for homepage');
        
        // Use enhanced API client with retry logic and caching
        const responseData = await apiClient.get('dashboards?limit=100');
        
        // Handle Supabase Edge Function response structure
        const allDashboards = responseData.data || responseData;
        
        if (!Array.isArray(allDashboards)) {
          throw new Error('Invalid dashboard data format received');
        }
        
        // Filter dashboards that have valid main images
        const dashboardsWithImages = allDashboards.filter(dashboard => 
          dashboard.mainImage && 
          dashboard.mainImage.trim() !== ''
        );
        
        if (dashboardsWithImages.length > 0) {
          // Shuffle the array to get random dashboards
          const shuffled = [...dashboardsWithImages].sort(() => 0.5 - Math.random());
          
          // Take the first 4 dashboards (or less if there aren't 4)
          const randomDashboards = shuffled.slice(0, 4);
          
          // Map to the format expected by the component
          const formattedDashboards = randomDashboards.map(dashboard => ({
            id: dashboard._id,
            title: dashboard.title,
            category: dashboard.category,
            thumbnail: dashboard.mainImage,
            slug: dashboard.slug
          }));
          
          setDashboards(formattedDashboards);
          debugLog(`Loaded ${formattedDashboards.length} random dashboards`);
        } else {
          errorLog('No dashboards with images found, using fallback dashboards');
          // Fallback to default dashboards if no dashboards with images are found
          setDashboards([
            { id: 1, title: "Financial Analytics", category: "Finance", thumbnail: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=300&h=200&fit=crop" },
            { id: 2, title: "Sales Performance", category: "Sales", thumbnail: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=300&h=200&fit=crop" },
            { id: 3, title: "Operations Dashboard", category: "Operations", thumbnail: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=200&fit=crop" },
            { id: 4, title: "Marketing Analytics", category: "Marketing", thumbnail: "https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?w=300&h=200&fit=crop" },
          ]);
        }
      } catch (error) {
        errorLog('Error fetching random dashboards:', error);
        // Fallback to default dashboards on error
        setDashboards([
          { id: 1, title: "Financial Analytics", category: "Finance", thumbnail: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=300&h=200&fit=crop" },
          { id: 2, title: "Sales Performance", category: "Sales", thumbnail: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=300&h=200&fit=crop" },
          { id: 3, title: "Operations Dashboard", category: "Operations", thumbnail: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=200&fit=crop" },
          { id: 4, title: "Marketing Analytics", category: "Marketing", thumbnail: "https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?w=300&h=200&fit=crop" },
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRandomDashboards();
  }, []);

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900 flex items-center relative overflow-hidden">
        {/* Animated background effect */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-[40rem] h-[40rem] bg-blue-400/10 dark:bg-blue-600/20 rounded-full blur-3xl -top-40 -left-40 animate-pulse"></div>
          <div className="absolute w-[30rem] h-[30rem] bg-indigo-400/10 dark:bg-indigo-600/20 rounded-full blur-3xl top-1/2 left-3/4 animate-pulse" style={{animationDelay: '1.5s'}}></div>
          <div className="absolute w-[35rem] h-[35rem] bg-purple-400/10 dark:bg-purple-600/20 rounded-full blur-3xl -bottom-40 -right-40 animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>
        <div className="px-16 py-24 relative z-10 w-full">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-16">
            {/* Left side - Text content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="w-full lg:w-[50%] text-left lg:pr-12"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-blue-600 dark:text-blue-400 mb-2 font-serif">
                Hello!
              </h2>
              <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl xl:text-7xl font-bold text-gray-900 dark:text-white mb-6 break-words whitespace-normal">
                this is Arbaz Ahmad
              </h1>
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-2xl md:text-3xl text-blue-600 dark:text-blue-400 font-semibold mb-6"
              >
                Power BI Specialist & Microsoft Fabric Super User
              </motion.p>
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-4xl leading-relaxed"
              >
                100+ dashboards. 7+ years of impact.
                <br />
                I transform complex business data into powerful, actionable insights using Power BI and Microsoft Fabric — helping teams turn metrics into meaning and dashboards into decisions.
              </motion.p>
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="flex flex-col sm:flex-row gap-6 mt-10"
              >
                <motion.div
                  whileHover={{ 
                    scale: 1.05,
                    y: -2,
                    transition: { type: "spring", stiffness: 400, damping: 25 }
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/gallery"
                    className="flex items-center justify-center space-x-2 px-10 py-4 rounded-xl text-lg font-semibold transition-all duration-300 shadow-sm hover:shadow-md bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white border border-blue-500 dark:border-blue-600 hover:border-blue-400 dark:hover:border-blue-500"
                  >
                    <Eye className="w-6 h-6" />
                    <span>View My Work</span>
                  </Link>
                </motion.div>
                <motion.div
                  whileHover={{ 
                    scale: 1.05,
                    y: -2,
                    transition: { type: "spring", stiffness: 400, damping: 25 }
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/contact"
                    className="flex items-center justify-center space-x-2 px-10 py-4 rounded-xl text-lg font-semibold transition-all duration-300 shadow-sm hover:shadow-md bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 hover:from-gray-200 hover:to-gray-300 dark:hover:from-gray-700 dark:hover:to-gray-600 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500"
                  >
                    <MessageSquare className="w-6 h-6" />
                    <span>Lets Connect</span>
                  </Link>
                </motion.div>
              </motion.div>
            </motion.div>
            
            {/* Right side - Dashboard Carousel */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="lg:w-1/2 relative lg:ml-12"
            >
              <DashboardCarousel />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Power BI Preview Section */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Live Dashboard Preview
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Experience the power of interactive data visualization with this featured Power BI dashboard
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <PowerBIPreview title="Interactive Business Analytics Dashboard" />
          </motion.div>
        </div>
      </section>

      {/* Featured Dashboards */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Featured Work
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              Explore my portfolio of 100+ dashboards across various industries
            </p>
          </motion.div>
          <div className="grid md:grid-cols-4 gap-6">
            {isLoading ? (
              // Loading state - show skeleton loaders
              [...Array(4)].map((_, index) => (
                <div key={index} className="bg-white dark:bg-gray-900 rounded-xl shadow-lg overflow-hidden animate-pulse">
                  <div className="w-full h-40 bg-gray-200 dark:bg-gray-700"></div>
                  <div className="p-4">
                    <div className="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                    <div className="h-5 w-36 bg-gray-200 dark:bg-gray-700 rounded mb-3"></div>
                    <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  </div>
                </div>
              ))
            ) : (
              // Loaded dashboards
              dashboards.map((dashboard, index) => (
                <motion.div
                  key={dashboard.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ 
                    scale: 1.02,
                    y: -4,
                    transition: { type: "spring", stiffness: 400, damping: 25 }
                  }}
                  className="bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 rounded-xl shadow-sm hover:shadow-md overflow-hidden transition-all duration-300 group border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 flex flex-col h-full"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={dashboard.thumbnail}
                      alt={dashboard.title}
                      className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors"></div>
                  </div>
                  <div className="p-4 flex flex-col flex-grow">
                    <div className="flex-grow">
                      <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">{dashboard.category}</span>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mt-1 mb-2 break-words break-all overflow-wrap-anywhere">{dashboard.title}</h3>
                    </div>
                    <motion.div
                      whileHover={{ 
                        scale: 1.05,
                        y: -2,
                        transition: { type: "spring", stiffness: 400, damping: 25 }
                      }}
                      whileTap={{ scale: 0.95 }}
                      className="mt-auto"
                    >
                      <Link 
                        to={dashboard.slug ? `/dashboard/${dashboard.slug}` : "/gallery"}
                        className="flex items-center space-x-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-300 shadow-sm hover:shadow-md bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 text-blue-600 dark:text-blue-400 hover:from-blue-100 hover:to-indigo-100 dark:hover:from-blue-800/40 dark:hover:to-indigo-800/40 border border-blue-200 dark:border-blue-700 hover:border-blue-300 dark:hover:border-blue-600"
                      >
                        <span>View Dashboard</span>
                        <ChevronRight className="w-3 h-3" />
                      </Link>
                    </motion.div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
          <div className="text-center mt-12">
            <motion.div
              whileHover={{ 
                scale: 1.05,
                y: -2,
                transition: { type: "spring", stiffness: 400, damping: 25 }
              }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/gallery"
                className="inline-flex items-center space-x-2 px-8 py-3 rounded-xl font-semibold transition-all duration-300 shadow-sm hover:shadow-md bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white border border-blue-500 dark:border-blue-600 hover:border-blue-400 dark:hover:border-blue-500"
              >
                <span>View All Dashboards</span>
                <ChevronRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

// About Page
const AboutPage = () => {
  const { aboutMeContent } = usePageContent();
  
  const skills = [
    { name: "Power BI", level: 95, description: "Advanced dashboard development, custom visuals, and enterprise solutions" },
    { name: "Microsoft Fabric", level: 90, description: "Data lakehouse architecture, real-time analytics, and unified data platform" },
    { name: "Advanced Excel", level: 94, description: "Power Query, Power Pivot, VBA, complex formulas, and advanced analytics" },
    { name: "DAX (Data Analysis Expressions)", level: 88, description: "Complex calculations, time intelligence, and advanced data modeling" },
    { name: "SQL & Database Management", level: 85, description: "Query optimization, stored procedures, and database design" },
    { name: "Data Modeling & Architecture", level: 90, description: "Star schema design, dimensional modeling, and data warehouse concepts" },

    { name: "Financial Analysis & Reporting", level: 92, description: "Financial modeling, budgeting, forecasting, and variance analysis" }
  ];

  // Use dynamic data from API or fallback to default values
  const achievements = aboutMeContent?.key_achievements?.map((achievement, index) => {
    // Assign icons in a rotating pattern
    const icons = [Award, BarChart3, Star, Calendar, User, Briefcase];
    return {
      icon: icons[index % icons.length],
      title: achievement.title,
      description: achievement.description
    };
  }) || [
    { icon: Award, title: "Microsoft Fabric Community Super User", description: "Recognized as a Super User in the Microsoft Fabric Community with 500+ helpful contributions and active participation in driving community growth" },
    { icon: BarChart3, title: "100+ Enterprise Dashboards", description: "Successfully delivered comprehensive analytics solutions across finance, retail, healthcare, and manufacturing industries" },
    { icon: Star, title: "8+ Years Analytics Expertise", description: "Deep expertise in financial analysis, business intelligence, and data-driven decision making across multiple domains" },
    { icon: Calendar, title: "Advanced Education", description: "MBA in Accounting & Finance and Master of Commerce degree, combining business acumen with technical expertise" },
    { icon: User, title: "Community Leadership", description: "Active mentor and trainer, helping 200+ professionals master Power BI, Excel, and data analytics through workshops and training programs" },
    { icon: Briefcase, title: "Cross-Industry Experience", description: "Proven track record in finance, retail, healthcare, manufacturing, and consulting with focus on ROI-driven analytics solutions" }
  ];

  const expertiseAreas = aboutMeContent?.areas_of_expertise || [
    {
      category: "Business Intelligence & Analytics",
      skills: ["Power BI Development", "Dashboard Design", "Data Visualization", "KPI Development", "Self-Service Analytics", "Mobile BI Solutions"]
    },
    {
      category: "Microsoft Excel Mastery",
      skills: ["Advanced Formulas & Functions", "Power Query (M Language)", "Power Pivot & Data Models", "VBA & Automation", "Financial Modeling", "Dynamic Dashboards"]
    },
    {
      category: "Data Platform & Architecture",
      skills: ["Microsoft Fabric", "Azure Data Factory", "Data Lakehouse Design", "Real-time Analytics", "Data Governance", "ETL/ELT Processes"]
    },
    {
      category: "Financial Analytics",
      skills: ["Financial Reporting", "Budget vs Actual Analysis", "Variance Analysis", "Cash Flow Modeling", "Risk Assessment", "Performance Metrics"]
    }
  ];

  return (
    <div className="pt-16">
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-2 lg:px-4">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-12"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">About Me</h1>
              <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-4xl mx-auto">
                {aboutMeContent?.intro || "I'm Arbaz, a dedicated data enthusiast and accomplished financial analyst with a passion for transforming complex business data into valuable insights and strategic decisions. My journey into the world of data began with curiosity about how information can drive business success, and has evolved into deep expertise across the Microsoft data platform."}
              </p>
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-8 mb-12">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="space-y-6"
              >
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Professional Journey</h2>
                <div className="space-y-4 text-gray-600 dark:text-gray-300 leading-relaxed">
                  {aboutMeContent?.professional_journey ? (
                    <div dangerouslySetInnerHTML={{ __html: aboutMeContent.professional_journey }} />
                  ) : (
                    <React.Fragment key="professional-journey">
                      <p key="experience">
                        With over 7 years of hands-on experience in finance and analytics, I specialize in transforming complex datasets into compelling visual narratives that drive strategic business decisions. My expertise spans across multiple industries including finance, retail, healthcare, and manufacturing.
                      </p>
                      <p key="community">
                        As a <strong className="text-gray-900 dark:text-white">Microsoft Fabric Community Super User</strong>, I actively contribute to the community by sharing knowledge, answering questions, and helping fellow professionals navigate the evolving landscape of data analytics. This recognition reflects my commitment to continuous learning and community building.
                      </p>
                      <p key="education">
                        My educational foundation includes an <strong className="text-gray-900 dark:text-white">MBA in Accounting & Finance</strong> and a <strong className="text-gray-900 dark:text-white">Master of Commerce degree</strong>, providing me with deep business acumen that perfectly complements my technical skills. This unique combination allows me to understand both the technical and business aspects of data analytics projects.
                      </p>
                      <p key="achievements">
                        I have successfully delivered <strong className="text-gray-900 dark:text-white">100+ enterprise-grade dashboards</strong> and analytics solutions, each tailored to specific business needs and designed to provide actionable insights that drive measurable ROI.
                      </p>
                    </React.Fragment>
                  )}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Core Technical Skills</h2>
                <div className="space-y-3">
                  {skills.map((skill, index) => (
                    <div key={skill.name} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-900 dark:text-white font-semibold text-sm">{skill.name}</span>
                        <span className="text-blue-600 dark:text-blue-400 font-bold text-sm">{skill.level}%</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2">
                        <motion.div
                          initial={{ width: 0 }}
                          whileInView={{ width: `${skill.level}%` }}
                          transition={{ duration: 1, delay: index * 0.1 }}
                          viewport={{ once: true }}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full"
                        ></motion.div>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{skill.description}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Expertise Areas */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">Areas of Expertise</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {expertiseAreas.map((area, index) => (
                  <motion.div
                    key={area.category || area.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ 
                      scale: 1.02,
                      y: -2,
                      transition: { type: "spring", stiffness: 400, damping: 25 }
                    }}
                    className="p-6 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-800 dark:to-blue-900/20 border border-gray-200 dark:border-gray-600 hover:border-blue-200 dark:hover:border-blue-700"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{area.category || area.title}</h3>
                    <ul className="space-y-2">
                      {area.skills.map((skill, skillIndex) => (
                        <li key={`${area.category}-${skillIndex}`} className="flex items-start text-sm text-gray-600 dark:text-gray-300">
                          <ChevronRight className="w-3 h-3 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                          {skill}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">Key Achievements & Recognition</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {achievements.map((achievement, index) => (
                  <motion.div
                    key={achievement.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ 
                      scale: 1.02,
                      y: -2,
                      transition: { type: "spring", stiffness: 400, damping: 25 }
                    }}
                    whileTap={{ scale: 0.95 }}
                    className="p-5 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-800 dark:to-blue-900/20 border border-gray-200 dark:border-gray-600 hover:border-blue-200 dark:hover:border-blue-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 dark:hover:from-blue-900/30 dark:hover:to-indigo-900/30 cursor-pointer"
                  >
                    <motion.div
                      whileHover={{
                        scale: 1.1,
                        rotate: 5,
                        transition: { type: "spring", stiffness: 300, damping: 20 }
                      }}
                    >
                      <achievement.icon className="w-8 h-8 text-blue-600 mb-3" />
                    </motion.div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">{achievement.title}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{achievement.description}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Gallery Page
// Legacy GalleryPage component - replaced by new Gallery component
// Keeping for reference during transition
const LegacyGalleryPage = () => {
  return <Gallery />;
};

// Services Page
const ServicesPage = () => {
  const { servicesContent } = usePageContent();
  
  const defaultServices = [
    {
      icon: BarChart3,
      title: "Dashboard Design & Development",
      description: "Custom Power BI and Excel dashboards tailored to your business needs with interactive visualizations and real-time data insights.",
      features: ["Custom Visualizations", "Real-time Data", "Interactive Reports", "Excel Dashboards", "Power Pivot Models", "Mobile Responsive"],
      color: "blue"
    },
    {
      icon: Briefcase,
      title: "Power BI Consulting",
      description: "Strategic consulting for Power BI implementation, data architecture, and best practices to maximize your analytics ROI.",
      features: ["Architecture Planning", "Best Practices", "Performance Optimization", "Data Governance", "Power Query Optimization", "Advanced Excel Integration"],
      color: "green"
    },
    {
      icon: User,
      title: "Training",
      description: "Comprehensive training programs for teams to master Power BI, Excel analytics, DAX, and advanced data visualization techniques with personalized learning paths.",
      features: ["Hands-on Training", "Custom Curriculum", "Advanced DAX Training", "Excel Analytics", "Power Query Mastery", "Power Pivot Training", "Advanced Excel Techniques", "1-on-1 Mentoring"],
      color: "purple"
    }
  ];

  const services = servicesContent?.services ? servicesContent.services.map((service, index) => ({
    ...service,
    icon: [BarChart3, Briefcase, User][index] || BarChart3,
    color: ["blue", "green", "purple"][index] || "blue"
  })) : defaultServices;

  return (
    <div className="pt-16">
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">Services</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              {servicesContent?.intro || "Comprehensive Power BI and data analytics services to transform your business intelligence"}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="rounded-xl p-8 group transition-all duration-300 shadow-sm hover:shadow-md bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-800 dark:to-blue-900/20 border border-gray-200 dark:border-gray-600 hover:border-blue-200 dark:hover:border-blue-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 dark:hover:from-blue-900/30 dark:hover:to-indigo-900/30 flex flex-col h-full"
                whileHover={{ 
                  scale: 1.05,
                  y: -2,
                  transition: { type: "spring", stiffness: 400, damping: 25 }
                }}
                whileTap={{ scale: 0.95 }}
              >
                <motion.div 
                  className={`w-16 h-16 bg-${service.color}-100 dark:bg-${service.color}-900/20 rounded-xl flex items-center justify-center mb-6 transition-all duration-300 shadow-sm hover:shadow-md border border-${service.color}-200 dark:border-${service.color}-700`}
                  whileHover={{
                    scale: 1.1,
                    rotate: 5,
                    transition: { type: "spring", stiffness: 300, damping: 20 }
                  }}
                >
                  <service.icon className={`w-8 h-8 text-${service.color}-600`} />
                </motion.div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{service.title}</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2 mb-6 flex-grow">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-center text-gray-600 dark:text-gray-300">
                      <ChevronRight className="w-4 h-4 text-blue-600 mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/contact" className="mt-auto">
                  <motion.button 
                    className="w-full py-3 px-6 rounded-xl font-semibold transition-all duration-300 shadow-sm hover:shadow-md bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 border border-blue-500 hover:border-blue-400"
                    whileHover={{ 
                      scale: 1.05,
                      transition: { type: "spring", stiffness: 400, damping: 25 }
                    }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Get Started
                  </motion.button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// Contact Page
const ContactPage = () => {
  const { contactInfo } = useContact();

  const contactMethods = [
    {
      icon: (
        <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      ),
      title: "LinkedIn",
      description: "Connect with me professionally",
      details: "Follow my latest insights on Power BI, Microsoft Fabric, and data analytics. Join my network of 10,000+ professionals.",
      action: "Connect on LinkedIn",
      link: contactInfo.linkedin,
      color: "text-blue-600",
      bgColor: "bg-blue-50 dark:bg-blue-900/20",
      borderColor: "border-blue-200 dark:border-blue-800"
    },
    {
      icon: (
        <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.89 3.488"/>
        </svg>
      ),
      title: "WhatsApp",
      description: "Quick messaging for urgent queries",
      details: "Get instant responses for quick questions, project discussions, or urgent support. Available during business hours (9 AM - 6 PM UTC).",
      action: "Message on WhatsApp",
      link: `https://api.whatsapp.com/send/?phone=${contactInfo?.whatsapp || '971552342984'}&text=Hi%20Arbaz%2C%0A%0AI%20recently%20came%20across%20your%20portfolio%20website%2C%20and%20I%20must%20say%2C%20your%20work%20is%20quite%20impressive%2E%0A%0AI%27m%20interested%20in%20discussing%20a%20potential%20project%20collaboration%20and%20would%20love%20to%20connect%20with%20you%20to%20explore%20how%20we%20can%20work%20together%2E%0A%0APlease%20let%20me%20know%20if%20you%27re%20available%20for%20a%20quick%20chat%20this%20week%2E%0A%0ALooking%20forward%20to%20hearing%20from%20you%21%0A%0ABest%20regards%2C%0A%5BYour%20Name%5D`,
      color: "text-green-500",
      bgColor: "bg-green-50 dark:bg-green-900/20",
      borderColor: "border-green-200 dark:border-green-800"
    },
    {
      icon: (
        <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
          <path d="M0 0v11.408h11.408V0H0zm12.594 0v11.408H24V0H12.594zM0 12.594V24h11.408V12.594H0zm12.594 0V24H24V12.594H12.594z"/>
        </svg>
      ),
      title: "Microsoft Fabric Community",
      description: "Community discussions & insights",
      details: "Join me in the Microsoft Fabric Community where I share advanced tips, answer questions, and discuss the latest features. Super User status with 500+ helpful answers.",
      action: "Visit Community Profile",
      link: contactInfo.fabricCommunity,
      color: "text-purple-600",
      bgColor: "bg-purple-50 dark:bg-purple-900/20",
      borderColor: "border-purple-200 dark:border-purple-800"
    },
    {
      icon: <Mail className="w-12 h-12" />,
      title: "Email",
      description: "Formal business inquiries",
      details: "For detailed project proposals, collaboration opportunities, or formal business communications. I typically respond within 24 hours.",
      action: "Send Email",
      link: `mailto:${contactInfo?.email || 'raaz.iglesias@gmail.com'}?subject=Collaboration%20Opportunity%20for%20a%20Project&body=Hi%20Arbaz%2C%0D%0A%0D%0AI%20recently%20visited%20your%20portfolio%20website%20and%20was%20very%20impressed%20with%20your%20work%2C%20especially%20your%20approach%20to%20data%20visualization%20and%20dashboard%20design.%0D%0A%0D%0AI%27m%20reaching%20out%20to%20discuss%20a%20potential%20project%20collaboration%20and%20would%20love%20to%20connect%20with%20you%20to%20explore%20how%20we%20can%20work%20together.%0D%0A%0D%0APlease%20let%20me%20know%20a%20convenient%20time%20for%20a%20quick%20call%20or%20email%20exchange.%0D%0A%0D%0ALooking%20forward%20to%20hearing%20from%20you.%0D%0A%0D%0ABest%20regards%2C%0D%0A%5BYour%20Name%5D`,
      color: "text-gray-600 dark:text-gray-400",
      bgColor: "bg-gray-50 dark:bg-gray-800/50",
      borderColor: "border-gray-200 dark:border-gray-700"
    }
  ];

  return (
    <div className="pt-16">
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">Get In Touch</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Ready to transform your data into actionable insights? Choose your preferred way to connect and let's discuss your Power BI project.
            </p>
          </motion.div>

          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Let's Connect</h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                Whether you need custom dashboards, consulting services, training, or have questions about Power BI and Microsoft Fabric, 
                I'm here to help you unlock the power of your data.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
              {contactMethods.map((method, index) => (
                <motion.div
                  key={method.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.1 * (index + 1) }}
                  whileHover={{
                    scale: 1.02,
                    transition: { type: "spring", stiffness: 400, damping: 25 }
                  }}
                  whileTap={{ scale: 0.98 }}
                  className={`${method.bgColor} ${method.borderColor} border-2 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 group cursor-pointer`}
                  onClick={() => window.open(method.link, '_blank')}
                >
                  <div className="flex flex-col items-center text-center">
                    <motion.div
                      className={`${method.color} mb-6 group-hover:scale-110 transition-transform duration-300`}
                      whileHover={{
                        rotate: [0, -10, 10, -10, 0],
                        transition: { duration: 0.5 }
                      }}
                    >
                      {method.icon}
                    </motion.div>
                    
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                      {method.title}
                    </h3>
                    
                    <p className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-4">
                      {method.description}
                    </p>
                    
                    <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                      {method.details}
                    </p>
                    
                    <motion.button
                      className={`${method.color} bg-white dark:bg-gray-800 border-2 ${method.borderColor} px-6 py-3 rounded-xl font-semibold hover:bg-gradient-to-r hover:from-blue-500 hover:to-purple-600 hover:text-white hover:border-transparent transition-all duration-300 shadow-lg hover:shadow-xl`}
                      whileHover={{
                        scale: 1.05,
                        transition: { type: "spring", stiffness: 400, damping: 25 }
                      }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {method.action}
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mt-16 text-center"
            >
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-2xl p-8 border border-blue-200 dark:border-blue-800">
                <div className="flex items-center justify-center mb-4">
                  <MapPin className="w-6 h-6 text-blue-600 mr-3" />
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">Global Availability</span>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  Available for remote projects worldwide • Timezone flexible • English & Hindi speaking
                </p>
                <div className="flex items-center justify-center space-x-6 text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                    <span>Usually responds within 2-4 hours</span>
                  </div>
                  <div className="flex items-center">
                    <Globe className="w-4 h-4 mr-2" />
                    <span>Remote collaboration expert</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Footer Component
const Footer = () => {
  const { contactInfo } = useContact();
  
  return (
    <footer className="bg-white dark:bg-gray-900 text-gray-800 dark:text-white py-12 transition-colors border-t border-gray-200 dark:border-gray-700">
      <div className="container mx-auto px-4">
        <div className="text-center">
          <div className="flex items-center justify-center space-x-3 mb-4 group">
            <motion.div
              className="relative"
              whileHover={{ 
                rotate: 360,
                scale: 1.3,
                transition: { 
                  type: "spring",
                  stiffness: 300,
                  damping: 20,
                  duration: 0.6
                }
              }}
            >
              <img 
                src={logoSvg} 
                alt="Arbaz Ahmad Logo" 
                className="w-16 h-16 logo-shimmer"
              />
            </motion.div>
            <span className="text-3xl font-bold text-gray-900 dark:text-white" style={{textShadow: '1px 1px 0px rgba(0,0,0,0.3)'}}>
              Arbaz Ahmad
            </span>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mb-6">Power BI Specialist & Microsoft Fabric Community Super User</p>
          <div className="flex justify-center space-x-6 mb-6">
            {/* LinkedIn */}
            <a href={contactInfo.linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 transition-colors" title="LinkedIn">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
            {/* WhatsApp */}
            <a href={`https://api.whatsapp.com/send/?phone=${contactInfo?.whatsapp || '971552342984'}&text=Hi%20Arbaz%2C%0A%0AI%20recently%20came%20across%20your%20portfolio%20website%2C%20and%20I%20must%20say%2C%20your%20work%20is%20quite%20impressive%2E%0A%0AI%27m%20interested%20in%20discussing%20a%20potential%20project%20collaboration%20and%20would%20love%20to%20connect%20with%20you%20to%20explore%20how%20we%20can%20work%20together%2E%0A%0APlease%20let%20me%20know%20if%20you%27re%20available%20for%20a%20quick%20chat%20this%20week%2E%0A%0ALooking%20forward%20to%20hearing%20from%20you%21%0A%0ABest%20regards%2C%0A%5BYour%20Name%5D`} target="_blank" rel="noopener noreferrer" className="text-green-500 hover:text-green-600 transition-colors" title="WhatsApp">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.89 3.488"/>
              </svg>
            </a>
            {/* Microsoft Fabric Community */}
            <a href={contactInfo.fabricCommunity} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 transition-colors" title="Microsoft Fabric Community">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M0 0v11.408h11.408V0H0zm12.594 0v11.408H24V0H12.594zM0 12.594V24h11.408V12.594H0zm12.594 0V24H24V12.594H12.594z"/>
              </svg>
            </a>
            {/* Email */}
            <a href={`mailto:${contactInfo?.email || 'raaz.iglesias@gmail.com'}?subject=Collaboration%20Opportunity%20for%20a%20Project&body=Hi%20Arbaz%2C%0D%0A%0D%0AI%20recently%20visited%20your%20portfolio%20website%20and%20was%20very%20impressed%20with%20your%20work%2C%20especially%20your%20approach%20to%20data%20visualization%20and%20dashboard%20design.%0D%0A%0D%0AI%27m%20reaching%20out%20to%20discuss%20a%20potential%20project%20collaboration%20and%20would%20love%20to%20connect%20with%20you%20to%20explore%20how%20we%20can%20work%20together.%0D%0A%0D%0APlease%20let%20me%20know%20a%20convenient%20time%20for%20a%20quick%20call%20or%20email%20exchange.%0D%0A%0D%0ALooking%20forward%20to%20hearing%20from%20you.%0D%0A%0D%0ABest%20regards%2C%0D%0A%5BYour%20Name%5D`} className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors" title="Email">
              <Mail className="w-6 h-6" />
            </a>
          </div>
          <p className="text-gray-500 dark:text-gray-500">© 2025 Arbaz Ahmad. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

// Page Wrapper with Animation
const PageWrapper = ({ children }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      {children}
    </motion.div>
  );
};



// ScrollToTop Component
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Main App Component
function App() {
  // Initialize visitor tracking
  useEffect(() => {
    initializeVisitorTracking();
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <ContactProvider>
          <PageContentProvider>
            <div className="App min-h-screen bg-white dark:bg-gray-900 transition-colors">
              <BrowserRouter>
                <ScrollToTop />
                <Navigation />
                <AnimatePresence mode="wait">
                  <Routes>
                    <Route path="/" element={<PageWrapper><HomePage /></PageWrapper>} />
                    <Route path="/about" element={<PageWrapper><AboutPage /></PageWrapper>} />
                    <Route path="/gallery" element={<PageWrapper><Gallery /></PageWrapper>} />
                    <Route path="/dashboard/:slug" element={<PageWrapper><DashboardDetail /></PageWrapper>} />
                    {/* Blog routes temporarily removed */}
                    {/* <Route path="/blog" element={<PageWrapper><BlogPage /></PageWrapper>} /> */}
                    {/* <Route path="/blog/:slug" element={<PageWrapper><BlogPostDetail /></PageWrapper>} /> */}
                    <Route path="/admin" element={<PageWrapper><AdminPanel /></PageWrapper>} />
                    {/* <Route path="/admin/blog/edit/:id" element={<PageWrapper><BlogEditor token={getAuthToken()} /></PageWrapper>} /> */}
                    <Route path="/services" element={<PageWrapper><ServicesPage /></PageWrapper>} />
                    <Route path="/contact" element={<PageWrapper><ContactPage /></PageWrapper>} />
                  </Routes>
                </AnimatePresence>
                <Footer />
              </BrowserRouter>
            </div>
          </PageContentProvider>
        </ContactProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;